import React, { useState } from 'react';
import { getEnvironmentInfo, apiRequest } from '../../config/api';
import { getCurrentTimestamp } from '../../utils/dateUtils';
import { generateInvoiceHtml } from '../../utils/invoiceHtmlGenerator';
import '../styles/EmailInvoice.css';

// Environment-aware n8n webhook URL configuration
const getN8nWebhookUrl = () => {
  const envInfo = getEnvironmentInfo();

  switch (envInfo.environment) {
    case 'development':
      return "https://gmc-space.app.n8n.cloud/webhook-test/send-invoice-email";
    case 'production':
      return "https://gmc-space.app.n8n.cloud/webhook/send-invoice-email";
    case 'staging':
    default:
      return "https://gmc-space.app.n8n.cloud/webhook/send-invoice-email";
  }
};

const EmailInvoiceStandalone = ({
  orderDetails,
  customerInfo,
  lineItemsData,
  paymentsData,
  wigDetails = {},
  onSuccess = () => {},
  onError = () => {},
  buttonClassName = "btn btn-warning rounded-pill shadow-sm"
}) => {
  const [isSending, setIsSending] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);
  const [emailCustomerInfo, setEmailCustomerInfo] = useState(null);
  const [customMessage, setCustomMessage] = useState('');

  const envInfo = getEnvironmentInfo();
  const finalWebhookUrl = getN8nWebhookUrl();
  const orderNumber = orderDetails?.orderNo || orderDetails?.OrderNo || orderDetails?.orderNumber;

  const handleOpenModal = () => {
    // Prepare customer info
    setEmailCustomerInfo({
      CustomerEmail: customerInfo.CustomerEmail || customerInfo.email || '',
      CustomerFullName: customerInfo.CustomerFullName ||
                       customerInfo.customerName ||
                       `${customerInfo.CustomerFirstName || ''} ${customerInfo.CustomerLastName || ''}`.trim(),
      CustomerPhone: customerInfo.CustomerPhone || customerInfo.phone || '',
      CustomerAddress: customerInfo.CustomerAddress || customerInfo.address || '',
      CustomerCity: customerInfo.CustomerCity || customerInfo.city || '',
      CustomerState: customerInfo.CustomerState || customerInfo.state || '',
      CustomerZipCode: customerInfo.CustomerZipCode || customerInfo.zip || ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
    setCustomMessage('');
  };

  const sendEmail = async () => {
    setIsSending(true);
    setError(null);

    try {
      // Validate required data
      if (!emailCustomerInfo?.CustomerEmail) {
        throw new Error('Customer email address is required');
      }

      if (!orderNumber) {
        throw new Error('Order number is required');
      }

      console.log('📧 Preparing to send email:', {
        environment: envInfo.environment,
        webhookUrl: finalWebhookUrl,
        customerEmail: emailCustomerInfo.CustomerEmail,
        orderNumber: orderNumber
      });

      // Generate HTML programmatically
      console.log('📄 Generating invoice HTML programmatically...');
      const invoiceHtml = await generateInvoiceHtml({
        orderDetails,
        customerInfo: emailCustomerInfo,
        lineItemsData,
        paymentsData,
        wigDetails
      });

      console.log('📄 Generating PDF from invoice HTML...');

      // Call backend API to generate PDF as base64
      const pdfResponse = await apiRequest(`${envInfo.apiUrl}/api/invoice/generate-pdf-base64`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invoiceHtml: invoiceHtml,
          orderNumber: orderNumber,
          options: {
            format: 'Letter',
            margin: {
              top: '0.5in',
              right: '0.5in',
              bottom: '0.5in',
              left: '0.5in'
            }
          }
        })
      });

      if (!pdfResponse.ok) {
        throw new Error(`Failed to generate PDF: ${pdfResponse.statusText}`);
      }

      const pdfData = await pdfResponse.json();

      if (!pdfData.success) {
        throw new Error(pdfData.message || 'Failed to generate PDF');
      }

      console.log('✅ PDF generated successfully:', {
        filename: pdfData.filename,
        size: pdfData.size
      });

      // Prepare data for n8n webhook
      const emailData = {
        // Customer info
        customerEmail: emailCustomerInfo.CustomerEmail,
        customerName: emailCustomerInfo.CustomerFullName || 'Valued Customer',
        customerId: orderDetails?.customerNo || orderDetails?.OrderCustomerNo,
        // Order info
        orderNumber: orderNumber,
        orderType: orderDetails?.orderType || orderDetails?.OrderType,

        // PDF attachment (base64 encoded)
        pdfAttachment: {
          filename: pdfData.filename,
          content: pdfData.pdfBase64,
          encoding: 'base64',
          contentType: 'application/pdf'
        },

        customMessage: customMessage.trim(),

        // Environment and routing info
        environment: envInfo.environment,
        sentAt: getCurrentTimestamp(),
        sentFrom: 'DiniTracker Application',

        // Additional metadata for n8n processing
        routing: {
          environment: envInfo.environment,
          hostname: envInfo.hostname,
          timestamp: getCurrentTimestamp()
        },

        // Include the order and customer data for n8n processing
        orderDetails: orderDetails,
        customerInfo: emailCustomerInfo
      };

      console.log('📧 Sending email via n8n webhook...');

      const response = await apiRequest(finalWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Environment': envInfo.environment,
          'X-Source': 'DiniTracker-EmailInvoice-Standalone'
        },
        body: JSON.stringify(emailData)
      });

      // Check response status first
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } else {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          }
        } catch (parseError) {
          console.warn('Could not parse error response:', parseError);
        }

        throw new Error(errorMessage);
      }

      // Handle successful response safely
      let result = { success: true, message: 'Email sent successfully' };

      try {
        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          const responseText = await response.text();

          if (responseText && responseText.trim()) {
            result = JSON.parse(responseText);
          }
        } else {
          const responseText = await response.text();
          console.log('Non-JSON response received:', responseText);
        }
      } catch (parseError) {
        console.warn('Could not parse success response JSON, but request succeeded:', parseError);
      }

      // Validate the result
      if (result && result.success === false) {
        throw new Error(result.message || result.error || 'Email sending failed');
      }

      console.log('✅ Email sent successfully:', result);

      // Success callback
      onSuccess({
        customerEmail: emailData.customerEmail,
        customerName: emailData.customerName,
        orderNumber: emailData.orderNumber,
        environment: envInfo.environment,
        result: result
      });

      // Close modal and reset
      setShowModal(false);
      setCustomMessage('');

      // Show success message
      alert(`Invoice email sent successfully to ${emailData.customerEmail}!`);

    } catch (err) {
      console.error('❌ Error sending email:', err);

      let userMessage = err.message;

      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        userMessage = `Network error (${envInfo.environment}). Please check your connection and try again.`;
      } else if (err.message.includes('404')) {
        userMessage = `Email service unavailable (${envInfo.environment}). Please try again later.`;
      } else if (err.message.includes('timeout')) {
        userMessage = 'Request timed out. Please try again.';
      } else if (err.message.includes('JSON input')) {
        userMessage = `Server response error (${envInfo.environment}). The email may have been sent. Please check and try again if needed.`;
      }

      setError(userMessage);
      onError({ ...err, environment: envInfo.environment, webhookUrl: finalWebhookUrl });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      {/* Email Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleOpenModal();
        }}
        disabled={!orderNumber || !customerInfo}
        className={buttonClassName}
        title={!orderNumber ? "No order number available" :
               !customerInfo ? "No customer information available" :
               "Send invoice via email"}
      >
        <i className="fas fa-envelope mr-1"></i> Email Invoice
      </button>

      {/* Email Modal */}
      {showModal && (
        <div className="email-modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="email-modal">
            <div className="email-modal-header">
              <h3>Send Invoice Email</h3>
              <button
                onClick={handleCloseModal}
                className="close-button"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="email-modal-content">
              {error ? (
                <div className="error-state">
                  <p className="error-message">❌ {error}</p>
                  <button onClick={() => setError(null)} className="retry-button">
                    Try Again
                  </button>
                </div>
              ) : (
                <div className="email-form">
                  {/* Editable Customer Info */}
                  <div className="customer-info-email">
                    <div className="info-row">
                      <label>To:</label>
                      <input
                        type="email"
                        value={emailCustomerInfo?.CustomerEmail || ''}
                        onChange={(e) => setEmailCustomerInfo(prev => ({
                          ...prev,
                          CustomerEmail: e.target.value
                        }))}
                        placeholder="customer@example.com"
                        className="customer-input"
                        required
                      />
                    </div>
                    <div className="info-row">
                      <label>Customer:</label>
                      <input
                        type="text"
                        value={emailCustomerInfo?.CustomerFullName || ''}
                        onChange={(e) => setEmailCustomerInfo(prev => ({
                          ...prev,
                          CustomerFullName: e.target.value
                        }))}
                        placeholder="Customer Name"
                        className="customer-input"
                      />
                    </div>
                    <div className="info-row">
                      <label>Order:</label>
                      <span>{orderNumber}</span>
                    </div>
                    {orderDetails?.orderType && (
                      <div className="info-row">
                        <label>Type:</label>
                        <span>{orderDetails.orderType}</span>
                      </div>
                    )}

                    {/* Environment info for debugging (only show in non-production) */}
                    {!envInfo.isProduction && (
                      <div className="info-row environment-info">
                        <label>Environment:</label>
                        <span className={`env-badge env-${envInfo.environment}`}>
                          {envInfo.environment.toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Custom Message */}
                  <div className="custom-message">
                    <label htmlFor="customMessage">Custom Message (Optional):</label>
                    <textarea
                      id="customMessage"
                      placeholder="Add a personal message to include with the invoice..."
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      rows="3"
                      maxLength="500"
                    />
                    <small>{customMessage.length}/500 characters</small>
                  </div>

                  {/* Action Buttons */}
                  <div className="modal-actions">
                    <button
                      onClick={handleCloseModal}
                      className="cancel-button"
                      disabled={isSending}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={sendEmail}
                      className="send-button"
                      disabled={isSending || !emailCustomerInfo?.CustomerEmail}
                    >
                      {isSending ? (
                        <>
                          <div className="spinner small"></div>
                          Sending...
                        </>
                      ) : (
                        '📧 Send Invoice Email'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmailInvoiceStandalone;
